import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import type { components } from '@/generated/bff';
import { portalApiClient } from '../api/portalApiClient';
import { clearSignedInHint, markSignedInHint, resetSessionExpiredGuard } from './portalSession';

export type OtpRequestCreate = components['schemas']['OtpRequestCreate'];
export type OtpRequestAck = components['schemas']['OtpRequestAck'];
export type OtpVerifyCreate = components['schemas']['OtpVerifyCreate'];
export type OtpVerifyAck = components['schemas']['OtpVerifyAck'];

const requestOtp = async (payload: OtpRequestCreate): Promise<OtpRequestAck> => {
  const { data } = await portalApiClient.post<OtpRequestAck>('/auth/rep/otp/request', payload);
  return data;
};

const verifyOtp = async (payload: OtpVerifyCreate): Promise<OtpVerifyAck> => {
  const { data } = await portalApiClient.post<OtpVerifyAck>('/auth/rep/otp/verify', payload);
  return data;
};

const logoutRequest = async (): Promise<void> => {
  await portalApiClient.post('/auth/rep/logout');
};

/** requirements §3.2: the BFF's own no-enumeration ack — "If this email is
 * registered..." every time, success or not. */
export const useRequestOtp = (): UseMutationResult<OtpRequestAck, unknown, OtpRequestCreate> =>
  useMutation({ mutationFn: requestOtp });

/** On success: mark the signed-in hint + reset the session-expiry guard (a
 * fresh sign-in is exactly the event that should let a FUTURE expiry
 * re-trigger the redirect) — the HttpOnly cookie itself is already set by
 * the server's own `Set-Cookie` response header before this resolves. */
export const useVerifyOtp = (): UseMutationResult<OtpVerifyAck, unknown, OtpVerifyCreate> =>
  useMutation({
    mutationFn: verifyOtp,
    onSuccess: () => {
      markSignedInHint();
      resetSessionExpiredGuard();
    },
  });

/** Idempotent server-side (a missing/already-expired session is a silent
 * no-op) — the local hint is cleared regardless of the server outcome. */
export const useLogout = (): UseMutationResult<void, unknown, void> =>
  useMutation({
    mutationFn: logoutRequest,
    onSettled: () => {
      clearSignedInHint();
      resetSessionExpiredGuard();
    },
  });
